import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  UsersRound,
  Building2,
  BarChart3,
  RefreshCw,
  Plus,
  Trash2,
  RotateCcw,
  Shield,
  KeyRound,
  Sparkles,
  Settings2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { HelpBubble } from '@/components/HelpBubble';
import api from '@/lib/api';

const PLAN_LABELS = { starter: 'Starter', growth: 'Growth', enterprise: 'Enterprise', legacy: 'Legacy' };

function fmt(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return ts; }
}

function StatusBadge({ status }) {
  const map = {
    active: 'bg-emerald-500/15 text-emerald-600',
    archived: 'bg-muted text-muted-foreground',
    trial: 'bg-amber-500/15 text-amber-600',
  };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${map[status] || map.active}`}>{status || 'active'}</span>;
}

function SeatsCell({ org, onSave }) {
  const initial = org.seats_unlimited ? '' : String(org.seats_raw ?? org.seats ?? '');
  const [val, setVal] = useState(initial);
  const [unlimited, setUnlimited] = useState(!!org.seats_unlimited);
  const commit = () => {
    if (unlimited) {
      if (!org.seats_unlimited) onSave(org, 0);
      return;
    }
    const n = parseInt(val, 10);
    if (!Number.isFinite(n) || n < 1) return;
    if (n !== (org.seats_raw ?? org.seats)) onSave(org, n);
  };
  return (
    <div className="flex items-center gap-2">
      <span className={`text-xs font-medium ${org.seats_full ? 'text-destructive' : 'text-muted-foreground'}`}>{org.members} /</span>
      {unlimited ? (
        <span className="text-xs font-semibold" title="Unlimited seats — no cap">∞</span>
      ) : (
        <input
          type="number"
          min={1}
          className="h-8 w-16 rounded border bg-background px-2 text-xs"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          data-testid="org-seats-input"
        />
      )}
      <label className="inline-flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer select-none" title="Toggle unlimited seats (0)">
        <input
          type="checkbox"
          className="h-3 w-3 accent-primary"
          checked={unlimited}
          onChange={(e) => {
            const next = e.target.checked;
            setUnlimited(next);
            if (next) onSave(org, 0);
          }}
          data-testid="org-seats-unlimited"
        />
        ∞
      </label>
    </div>
  );
}

export default function PlatformAdmin() {
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, uRes, gRes] = await Promise.all([
        api.get('/admin/platform/organizations', { params: { include_archived: showArchived } }),
        api.get('/admin/platform/users'),
        api.get('/admin/platform/usage'),
      ]);
      setOrgs(oRes.data?.organizations || []);
      setUsers(uRes.data?.users || []);
      setUsage(gRes.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load platform data');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const updatePlan = async (org, plan) => {
    try {
      await api.patch(`/admin/platform/organizations/${org.id}/plan`, { plan });
      toast.success(`${org.name} → ${PLAN_LABELS[plan] || plan}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update plan');
    }
  };

  const updateSeats = async (org, seats) => {
    const n = Number.isFinite(seats) ? seats : parseInt(seats, 10);
    if (Number.isNaN(n) || n < 0) { toast.error('Seats must be 0 or greater (0 = unlimited)'); return; }
    try {
      await api.patch(`/admin/platform/organizations/${org.id}/seats`, { seats: n });
      toast.success(n === 0 ? `${org.name}: unlimited seats` : `${org.name}: seat cap → ${n}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update seats');
    }
  };

  const archive = async (org) => {
    try {
      await api.post(`/admin/platform/organizations/${org.id}/archive`);
      toast.success(`${org.name} archived`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to archive');
    }
  };

  const restore = async (org) => {
    try {
      await api.post(`/admin/platform/organizations/${org.id}/restore`);
      toast.success(`${org.name} restored`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restore');
    }
  };

  return (
    <div className="space-y-6" data-testid="platform-admin-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <UsersRound className="w-5 h-5 text-primary" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">Platform Admin</h1>
            <Badge variant="secondary" className="ml-1">Super User</Badge>
            <HelpBubble text="This is the single control center for tenants, users, and platform-wide usage. Only Super Admins can see this page." />
          </div>
          <p className="text-sm text-muted-foreground mt-1">Manage tenants, users, and platform-wide usage.</p>
        </div>
      </div>

      <Tabs defaultValue="orgs">
        <TabsList>
          <TabsTrigger value="orgs" data-testid="tab-orgs"><Building2 className="w-3.5 h-3.5 mr-1.5" /> Organizations</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><UsersRound className="w-3.5 h-3.5 mr-1.5" /> Users</TabsTrigger>
          <TabsTrigger value="usage" data-testid="tab-usage"><BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Usage</TabsTrigger>
        </TabsList>

        {/* ORGANIZATIONS */}
        <TabsContent value="orgs" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <span>{orgs.length} organizations</span>
              <HelpBubble text="A tenant is one company using SpendLens. Each has its own data, users, plan, and seats." />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowArchived((v) => !v)} data-testid="orgs-toggle-archived">
                {showArchived ? 'Hide archived' : 'Show archived'}
              </Button>
              <Button variant="outline" size="sm" onClick={load} data-testid="orgs-refresh">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
              </Button>
              <Button size="sm" onClick={() => (window.location.href = '/organizations?new=1')} data-testid="orgs-new">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New Organization
              </Button>
              <HelpBubble text="Opens the tenant creation form where you set name, industry, currency, and pick premium features. This is a sub-page of Platform Admin." />
            </div>
          </div>

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>
                      <div className="inline-flex items-center gap-1">Plan <HelpBubble text="Change the tenant's tier. Seats and any tier-bundled premium features flow from this." /></div>
                    </TableHead>
                    <TableHead>
                      <div className="inline-flex items-center gap-1">Status <HelpBubble text="Active = full access. Archived = tenant read-only for audits but no logins." /></div>
                    </TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>
                      <div className="inline-flex items-center gap-1">Seats <HelpBubble text="Edit the seat cap inline — plan default fills in if empty. Tick ∞ to make it unlimited (0). Red when the tenant is at capacity." /></div>
                    </TableHead>
                    <TableHead>
                      <div className="inline-flex items-center gap-1">Last activity <HelpBubble text="Most recent audit-logged action by any user in this tenant." /></div>
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && orgs.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-sm text-muted-foreground text-center py-6">Loading…</TableCell></TableRow>
                  )}
                  {orgs.map((o) => (
                    <TableRow key={o.id} data-testid="org-row">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {o.parent_organization_id ? (
                            <span className="text-muted-foreground text-sm ml-2" aria-hidden>↳</span>
                          ) : null}
                          <div>
                            <div className="font-medium flex items-center gap-1.5">
                              {o.name}
                              {o.org_type && o.org_type !== 'independent' ? (
                                <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
                                  o.org_type === 'holding' ? 'bg-primary/15 text-primary' :
                                  o.org_type === 'subsidiary' ? 'bg-blue-500/15 text-blue-600' :
                                  'bg-violet-500/15 text-violet-600'
                                }`}>{o.org_type}</span>
                              ) : null}
                            </div>
                            <div className="text-[10px] text-muted-foreground font-mono">
                              {o.short_id}{o.parent_organization_name ? ` • of ${o.parent_organization_name}` : ''}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select value={o.plan} onValueChange={(v) => updatePlan(o, v)}>
                          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="org-plan-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(PLAN_LABELS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><StatusBadge status={o.status} /></TableCell>
                      <TableCell>{o.members}</TableCell>
                      <TableCell className={o.seats_full ? 'text-destructive font-semibold' : ''}>
                        <SeatsCell org={o} onSave={updateSeats} />
                      </TableCell>
                      <TableCell className="text-xs">{fmt(o.last_activity_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => (window.location.href = `/organizations?edit=${o.id}`)}
                          title="Manage premium features"
                          data-testid="org-features-btn"
                        >
                          <Settings2 className="w-4 h-4 text-primary" />
                        </Button>
                        {o.status === 'archived' ? (
                          <Button size="icon" variant="ghost" onClick={() => restore(o)} title="Restore" data-testid="org-restore-btn">
                            <RotateCcw className="w-4 h-4 text-emerald-600" />
                          </Button>
                        ) : (
                          <Button size="icon" variant="ghost" onClick={() => archive(o)} title="Archive" data-testid="org-archive-btn">
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <span>{users.length} users</span>
              <HelpBubble text="Every user across every tenant. Deactivate here to instantly revoke access without deleting audit history." />
            </div>
            <Button variant="outline" size="sm" onClick={() => (window.location.href = '/user-management')} data-testid="users-manage">
              Full User Management
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>
                      <div className="inline-flex items-center gap-1">MFA <HelpBubble text="Whether the user has enrolled a TOTP authenticator app. Encourage enrolment to keep audit-safe." /></div>
                    </TableHead>
                    <TableHead>Last login</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid="user-row">
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell className="text-sm">{u.organization_name}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(u.roles || []).map((r) => (
                            <Badge key={r} variant="outline" className="text-[10px]">{r}</Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {u.mfa_enabled
                          ? <Badge className="bg-emerald-500/15 text-emerald-600"><Shield className="w-3 h-3 mr-1" /> On</Badge>
                          : <Badge variant="outline"><KeyRound className="w-3 h-3 mr-1" /> Off</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">{fmt(u.last_login_at)}</TableCell>
                      <TableCell>
                        {u.is_active === false
                          ? <Badge variant="destructive">Disabled</Badge>
                          : <Badge variant="secondary">Active</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* USAGE */}
        <TabsContent value="usage" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <UsageKpi label="Organizations" value={usage?.totals?.organizations} sub={`${usage?.totals?.active_organizations || 0} active`} help="Total tenants provisioned on the platform." />
            <UsageKpi label="Total users" value={usage?.totals?.users} sub={`${usage?.totals?.active_users_30d || 0} active in 30d`} help="Active = logged in at least once in the last 30 days." />
            <UsageKpi label="Transactions stored" value={usage?.totals?.transactions?.toLocaleString?.() || usage?.totals?.transactions} help="Total transaction rows across all tenants." />
            <UsageKpi
              label="AI calls today"
              value={usage?.ai?.calls_today}
              sub={`≈ $${((usage?.ai?.calls_today || 0) * 0.10).toFixed(2)} est.`}
              help="Universal LLM Key calls today across the platform. Estimated at $0.10/call — tune AI_AVG_COST_PER_CALL_USD to match your actual GPT-5.2 spend."
              highlight
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <UsageKpi label="Failed logins 24h" value={usage?.security?.failed_logins_24h} help="Any authentication rejection in the last 24 hours." />
            <UsageKpi label="MFA adoption" value={`${usage?.security?.mfa_adoption_pct || 0}%`} help="Share of users with MFA (TOTP) enrolled." />
            <UsageKpi label="Unresolved abuse" value={usage?.security?.unresolved_abuse_events} help="Events awaiting your review on the Abuse Events page." link="/admin/abuse-events" />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" /> Top LLM-consuming tenants today
                <HelpBubble text="Sorted by AI call count. Use this to spot orgs that may need a bigger plan or a chat with the customer success team." />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(usage?.ai?.by_org_today || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No AI activity yet today.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organization ID</TableHead>
                      <TableHead className="text-right">AI calls today</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(usage?.ai?.by_org_today || []).map((r) => (
                      <TableRow key={r.organization_id}>
                        <TableCell className="font-mono text-xs">{r.organization_id?.slice(0, 8) || '—'}</TableCell>
                        <TableCell className="text-right font-semibold">{r.calls}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary" /> Feature adoption across tenants
                <HelpBubble text="How many organizations subscribe to each premium feature. Deep-links to the Subscriptions view where you can toggle features per org." />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button variant="outline" size="sm" onClick={() => (window.location.href = '/organizations?tab=subs')} data-testid="feature-adoption-link">
                Open Subscription Overview
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Shows adoption rate per feature (AI Recommendations, Action Plans, P2P Analytics, Benchmark, etc.) and lets you manage each tenant\'s bundle.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UsageKpi({ label, value, sub, help, highlight, link }) {
  const body = (
    <Card className={highlight ? 'border-primary/40 bg-primary/5' : ''}>
      <CardContent className="p-4">
        <div className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground">
          {label}
          {help ? <HelpBubble text={help} /> : null}
        </div>
        <div className="text-2xl font-semibold mt-1">{value ?? '—'}</div>
        {sub ? <div className="text-xs text-muted-foreground mt-0.5">{sub}</div> : null}
      </CardContent>
    </Card>
  );
  if (link) {
    return <a href={link} className="block hover:opacity-80 transition-opacity">{body}</a>;
  }
  return body;
}
